import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { Pause, Play, ChevronLeft, ChevronRight } from "lucide-react";

const AUTOPLAY_MS = 4500;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export function HeroCarousel({ cards }) {
  const reducedMotion = usePrefersReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(typeof document !== "undefined" && document.hidden);
  const timerRef = useRef(null);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const onVis = () => setIsTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const shouldAutoplay = !reducedMotion && !manuallyPaused && !isHovered && !isFocused && !isTabHidden;

  useEffect(() => {
    if (!emblaApi || !shouldAutoplay) return undefined;
    timerRef.current = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [emblaApi, shouldAutoplay]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); emblaApi?.scrollPrev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); emblaApi?.scrollNext(); }
  };

  return (
    <section
      data-testid="home-hero-carousel"
      aria-roledescription="carousel"
      aria-label="Endure Beyond highlights"
      className="relative w-full bg-black overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsFocused(false); }}
      onKeyDown={handleKeyDown}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-3 sm:-ml-4">
          {cards.map((card, i) => (
            <div
              key={card.testId}
              className="min-w-0 shrink-0 grow-0 basis-[88%] sm:basis-[83%] lg:basis-[82%] pl-3 sm:pl-4"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${cards.length}: ${card.title}`}
            >
              <Link
                to={card.link}
                data-testid={card.testId}
                className="group relative block w-full aspect-square sm:aspect-[16/9] lg:aspect-[21/9] rounded-lg sm:rounded-xl overflow-hidden"
              >
                <img
                  src={card.image}
                  alt={card.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 lg:p-10">
                  <h2
                    className="font-display text-display-sm sm:text-display-md uppercase text-white mb-2 max-w-lg"
                    style={{ textShadow: "0 2px 16px rgba(0,0,0,0.9)" }}
                  >
                    {card.title}
                  </h2>
                  <p
                    className="font-body text-sm sm:text-base text-white/85 max-w-md"
                    style={{ textShadow: "0 1px 10px rgba(0,0,0,0.9)" }}
                  >
                    {card.description}
                  </p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <button
        data-testid="home-carousel-prev"
        aria-label="Previous slide"
        onClick={() => emblaApi?.scrollPrev()}
        className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 border border-white/20 items-center justify-center text-white hover:bg-black/60 transition-colors duration-200 z-10"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        data-testid="home-carousel-next"
        aria-label="Next slide"
        onClick={() => emblaApi?.scrollNext()}
        className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 border border-white/20 items-center justify-center text-white hover:bg-black/60 transition-colors duration-200 z-10"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      <div className="absolute bottom-3 sm:bottom-5 right-[14%] sm:right-[19%] lg:right-[20%] flex items-center gap-3 z-10">
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Choose slide">
          {cards.map((card, i) => (
            <button
              key={card.testId}
              data-testid={`home-carousel-dot-${i}`}
              role="tab"
              aria-selected={selectedIndex === i}
              aria-label={`Go to slide ${i + 1}: ${card.title}`}
              onClick={() => emblaApi?.scrollTo(i)}
              className="relative w-6 h-6 flex items-center justify-center"
            >
              <span className={`h-1.5 rounded-full transition-all duration-300 ${selectedIndex === i ? "w-5 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"}`} />
            </button>
          ))}
        </div>
        {!reducedMotion && (
          <button
            data-testid="home-carousel-pause-toggle"
            aria-label={manuallyPaused ? "Play carousel" : "Pause carousel"}
            aria-pressed={manuallyPaused}
            onClick={() => setManuallyPaused((p) => !p)}
            className="w-7 h-7 rounded-full bg-black/50 border border-white/25 flex items-center justify-center text-white hover:bg-black/70 transition-colors duration-200"
          >
            {manuallyPaused ? <Play className="w-3 h-3 ml-0.5" /> : <Pause className="w-3 h-3" />}
          </button>
        )}
      </div>
    </section>
  );
}
